/**
 * Rose Theme
 *
 * Soft, elegant pink and rose tones.
 */

import type { AppTheme } from './types';

export const roseTheme: AppTheme = {
  name: 'rose',
  displayName: 'Rose',
  description: 'Soft, elegant pink accents',

  antdTheme: {
    token: {
      colorPrimary: '#e11d48',
      colorSuccess: '#10b981',
      colorWarning: '#f59e0b',
      colorError: '#dc2626',
      colorInfo: '#ec4899',
      colorLink: '#e11d48',

      fontSize: 14,
      fontSizeHeading1: 28,
      fontSizeHeading2: 22,
      fontSizeHeading3: 18,
      fontSizeHeading4: 16,
      fontSizeHeading5: 14,
      fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif',
      fontWeightStrong: 600,

      padding: 16,
      paddingLG: 24,
      paddingXL: 32,
      margin: 16,
      marginLG: 24,
      marginXL: 32,

      borderRadius: 10,
      borderRadiusLG: 12,
      borderRadiusSM: 8,
      borderRadiusXS: 4,

      colorBgContainer: '#ffffff',
      colorBgElevated: '#ffffff',
      colorBgLayout: '#fff1f2',
      colorBorder: '#fecdd3',
      colorBorderSecondary: '#ffe4e6',

      colorText: '#881337',
      colorTextSecondary: '#9f1239',
      colorTextTertiary: '#fda4af',
      colorTextQuaternary: '#fecdd3',

      boxShadow: '0 1px 3px rgba(0, 0, 0, 0.04)',
      boxShadowSecondary: '0 1px 2px rgba(0, 0, 0, 0.03)',
    },

    components: {
      Layout: {
        headerBg: '#be123c',
        headerColor: '#ffffff',
        headerHeight: 48,
        headerPadding: '0 20px',
        siderBg: '#881337',
        bodyBg: '#fff1f2',
      },

      Button: {
        borderRadius: 10,
        borderRadiusLG: 12,
        borderRadiusSM: 8,
        controlHeight: 36,
        controlHeightLG: 42,
        controlHeightSM: 28,
        fontWeight: 500,
        primaryShadow: 'none',
      },

      Card: {
        borderRadius: 12,
        borderRadiusLG: 14,
        colorBgContainer: '#ffffff',
        boxShadowTertiary: 'none',
        padding: 20,
        paddingLG: 24,
      },

      Table: {
        headerBg: '#fff1f2',
        headerColor: '#fda4af',
        headerBorderRadius: 10,
        borderRadius: 10,
        rowHoverBg: '#fff1f2',
        cellPaddingBlock: 12,
        cellPaddingInline: 16,
      },

      Input: {
        borderRadius: 10,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Select: {
        borderRadius: 10,
        controlHeight: 36,
        controlHeightLG: 42,
      },

      Tag: {
        borderRadiusSM: 6,
        fontSizeSM: 12,
      },

      Modal: {
        borderRadiusLG: 14,
        boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      },

      Drawer: {
        colorBgElevated: '#ffffff',
      },

      Badge: {
        fontSize: 12,
      },

      Breadcrumb: {
        fontSize: 13,
        itemColor: '#fda4af',
        linkColor: '#e11d48',
      },

      Segmented: {
        borderRadius: 10,
        itemSelectedBg: '#e11d48',
        itemSelectedColor: '#ffffff',
      },

      Tabs: {
        itemColor: '#fda4af',
        itemSelectedColor: '#881337',
        itemHoverColor: '#9f1239',
      },

      Dropdown: {
        borderRadiusLG: 10,
      },

      Tooltip: {
        borderRadius: 8,
      },

      Divider: {
        colorSplit: '#ffe4e6',
      },
    },
  },

  cssVariables: {
    '--layout-header-bg': '#be123c',
    '--layout-sidebar-bg': '#881337',
    '--layout-content-bg': '#fff1f2',

    '--surface-0': '#fff1f2',
    '--surface-1': '#ffffff',
    '--surface-2': '#ffffff',
    '--surface-3': '#ffffff',

    '--text-primary': '#881337',
    '--text-secondary': '#9f1239',
    '--text-tertiary': '#fda4af',
    '--text-muted': '#fecdd3',

    '--border-default': '#fecdd3',
    '--border-subtle': '#ffe4e6',
    '--border-strong': '#fda4af',

    '--border-color': '#fecdd3',
    '--border-radius': '10px',

    '--shadow-sm': '0 1px 2px rgba(0, 0, 0, 0.03)',
    '--shadow-md': '0 1px 3px rgba(0, 0, 0, 0.04)',
    '--shadow-lg': '0 4px 16px rgba(0, 0, 0, 0.08)',

    '--accent-warm': '#f59e0b',
    '--accent-cool': '#ec4899',

    '--status-success': '#10b981',
    '--status-warning': '#f59e0b',
    '--status-error': '#dc2626',
    '--status-info': '#ec4899',

    '--status-success-subtle': '#ecfdf5',
    '--status-warning-subtle': '#fffbeb',
    '--status-error-subtle': '#fef2f2',
    '--status-info-subtle': '#fdf2f8',

    '--control-bg': '#fff1f2',
    '--control-border': '#fecdd3',
    '--control-hover': '#ffe4e6',

    '--hover-bg': '#fff1f2',
    '--active-bg': '#ffe4e6',
  },
};
